import type { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';

import type { ChannelSubscriptionLevel } from '../../types/ChannelSubscriptionLevel';
import type { CommunityChannelMember } from '../../types/CommunityChannelMember';
import { CommunityChannelMemberStatus } from '../../types/CommunityChannelMemberStatus';
import { hasChannelSubscriptionAccess } from './hasChannelSubscriptionAccess';

/**
 * Whether a viewer's standing satisfies a channel's subscription gate — the web
 * port of PopshopRN's `hasSubscriptionAccess` in `useJoinCommunityChannel`.
 *
 * Access is granted when the viewer's current level meets the channel's live
 * `minimumSubscriptionLevel`, OR — for an already-active member — when it still
 * meets the level SNAPSHOTTED at join time (`member.minimumRequiredSubscriptionLevel`).
 * The snapshot is the grandfathering carve-out: a creator who raises the bar
 * "for new users only" leaves the snapshot untouched so existing members keep
 * access, while a raise "for all users" bumps the snapshot and drops them. A
 * member whose actual subscription fell below even their own snapshot loses
 * access — matching the backend, which returns an empty feed in that case.
 *
 * Pure — the viewer's level + membership are resolved by the caller.
 */
export function memberHasChannelSubscriptionAccess(params: {
  viewerLevel: PopStoreExtendedSubscriptionLevel | undefined;
  channelMinimumSubscriptionLevel: ChannelSubscriptionLevel;
  member: CommunityChannelMember | null;
}): boolean {
  const { viewerLevel, channelMinimumSubscriptionLevel, member } = params;

  if (
    hasChannelSubscriptionAccess(viewerLevel, channelMinimumSubscriptionLevel)
  ) {
    return true;
  }
  if (member?.status !== CommunityChannelMemberStatus.Active) return false;
  return hasChannelSubscriptionAccess(
    viewerLevel,
    member.minimumRequiredSubscriptionLevel,
  );
}
